import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import './UpdateDetail.css';
import './UpdateDetail.responsive.css';

/**
 * Renderiza markdown simples: **texto** → <strong>texto</strong>
 */
function renderMarkdown(text) {
  if (!text) return null;
  const parts = text.split(/\*\*(.*?)\*\*/g);
  return parts.map((part, i) =>
    i % 2 === 1 ? <strong key={i}>{part}</strong> : part
  );
}

function isBlobUrlExpired(url) {
  if (!url || url.startsWith('/')) return false; // caminho local, nunca expira
  try {
    const se = new URL(url).searchParams.get('se');
    if (!se) return false;
    return new Date(se) < new Date();
  } catch {
    return false;
  }
}

/**
 * FiveUpdatePopups — versão dinâmica, orientada pelo banco de dados.
 *
 * Props:
 *  - isOpen        {boolean}   Controla visibilidade do modal
 *  - stepsData     {Array}     Array de steps vindos da API (já filtrado/ordenado)
 *  - config        {Object}    { allow_skip, show_dots } vindos da API
 *  - language      {string}    Código do idioma atual, ex: 'pt' | 'en'
 *  - onTestNow     {Function}  Callback do botão de ação no último step
 *  - onSeeLater    {Function}  Callback do botão "ver depois"
 *  - initialStep   {number}    Step inicial (padrão 0)
 *  - singleMode    {boolean}   Modo popup único sem navegação
 *  - onClose       {Function}  Callback ao clicar fora (só em singleMode)
 */
function FiveUpdatePopups({
  isOpen,
  stepsData = [],
  config = { allow_skip: true, show_dots: true },
  language = 'pt',
  onTestNow,
  onSeeLater,
  initialStep = 0,
  singleMode = false,
  onClose,
}) {


  const [step, setStep] = useState(initialStep);
  const { t: tHub } = useTranslation('hub');
  const lang = (language || 'pt').split('-')[0];

  useEffect(() => {
    setStep(initialStep);
  }, [initialStep]);

  if (!isOpen || stepsData.length === 0) return null;

  // Ordena por step_order e pega o conteúdo do idioma atual
  const steps = [...stepsData]
    .sort((a, b) => a.step_order - b.step_order)
    .map((s) => {
      // Suporta tanto string quanto objeto por idioma
      const mediaUrl =
        typeof s.media_url === 'object' && s.media_url !== null
          ? s.media_url[lang] ?? s.media_url['en'] ?? null
          : s.media_url;

      const resolvedMediaUrl = isBlobUrlExpired(mediaUrl) ? null : mediaUrl;

      return {
        mediaUrl: resolvedMediaUrl,
        mediaAlt: s.media_alt,
        content: s.content[lang] || s.content['en'] || {},
      };
    });

  const current = steps[step];

  if (!current || !current.content) return null;

  const { title, paragraphs = [], link, cta_text } = current.content;
  const isLastStep = step === steps.length - 1;

  const goPrev = () => setStep((s) => Math.max(0, s - 1));
  const goNext = () => setStep((s) => Math.min(steps.length - 1, s + 1));

  const handleOverlayClick = () => {
    if (singleMode && onClose) onClose();
  };


  return (
    <div
      className="update-detail-overlay"
      data-testid="popup-modal"
      onClick={handleOverlayClick}
    >
      <div
        className="update-detail-content"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Mídia (GIF ou imagem) */}
        <div
          className={`update-detail-gif-placeholder ${step === 0 ? 'first-popup-placeholder' : ''
            }`}
        >
          {current.mediaUrl && (
            <img src={current.mediaUrl} alt={current.mediaAlt || title} />
          )}
        </div>

        {/* Conteúdo textual */}
        <div className="update-detail-text-wrapper">
          <h1 className="update-detail-title">{title}</h1>

          <div className="update-detail-body-content">
            {paragraphs.map((paragraph, i) => (
              <p key={i} style={{ textAlign: 'justify' }}>
                {renderMarkdown(paragraph)}
              </p>
            ))}

            {link && (
              <p style={{ textAlign: 'justify' }}>
                <a
                  href={link.url}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {link.text}
                </a>
              </p>
            )}
          </div>
        </div>

        {/* Controles de navegação */}
        {!singleMode && (
          <div className="update-detail-controls">
            {step > 0 && (
              <button
                type="button"
                className="update-detail-prev update-detail-arrow"
                data-testid="voltar-popup"
                onClick={goPrev}
                aria-label="Anterior"
                title="Anterior"
              >
                <svg
                  width="18"
                  height="18"
                  viewBox="0 0 24 24"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                  aria-hidden="true"
                >
                  <path
                    d="M15 18L9 12L15 6"
                    stroke="#0067FF"
                    strokeWidth="2.2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </button>
            )}

            {isLastStep ? (
              <div className="update-detail-final-buttons">
                <button
                  type="button"
                  className="update-detail-test"
                  onClick={onTestNow}
                >
                  {cta_text ? cta_text : 'Testar agora'}
                </button>
              </div>
            ) : (
              <button
                type="button"
                className="update-detail-next update-detail-arrow"
                data-testid="next-popup"
                onClick={goNext}
                aria-label="Próximo"
                title="Próximo"
              >
                <svg
                  width="18"
                  height="18"
                  viewBox="0 0 24 24"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                  aria-hidden="true"
                >
                  <path
                    d="M9 6L15 12L9 18"
                    stroke="#0067FF"
                    strokeWidth="2.2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </button>
            )}
          </div>
        )}

        {/* Dots de paginação */}
        {!singleMode && config.show_dots && (
          <>
            <div className="update-detail-dots">
              {steps.map((_, i) => (
                <button
                  key={i}
                  type="button"
                  className={`update-detail-dot ${i === step ? 'active' : ''}`}
                  data-testid="dot"
                  aria-label={`Ir para passo ${i + 1}`}
                  onClick={() => setStep(i)}
                />
              ))}
            </div>

            {isLastStep && config.allow_skip && (
              <button
                type="button"
                className="update-detail-skip"
                data-testid="see-later"
                onClick={onSeeLater}
              >
                {tHub('updates_see_later')}
              </button>
            )}
          </>
        )}
      </div>
    </div>
  );
}

export default FiveUpdatePopups;